<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { debounce } from 'lodash';
import { ElMessage, ElMessageBox } from 'element-plus';

import HeaderTitle from '@/components/layout/HeaderTitle';
import SharedElementPagination from '@/components/shared/SharedElementPagination.vue';
import InvoiceImportDialog from '@/components/shared/Invoice/InvoiceImportDialog.vue';

import InvoiceService from '@/api/services/invoice.service';
import { useInvoiceStore } from '@/app/stores/invoiceStore';
import { useUiStore } from '@/app/stores/uiStore';

const router = useRouter();
const invoiceStore = useInvoiceStore();
const uiStore = useUiStore();

const invoices = ref([]);
const totalResults = ref(0);
const globalKpis = ref(null);
const page = ref(invoiceStore.invoiceFilters.page ?? 1);
const itemsPerPage = ref(invoiceStore.invoiceFilters.itemsPerPage ?? 20);

const filterNumber = ref(invoiceStore.invoiceFilters.number ?? '');
const filterClient = ref(invoiceStore.invoiceFilters.client ?? '');
const filterStatus = ref(invoiceStore.invoiceFilters.status ?? '');
const filterAmountMin = ref(invoiceStore.invoiceFilters.amountMin ?? '');
const filterAmountMax = ref(invoiceStore.invoiceFilters.amountMax ?? '');

const showImportDialog = ref(false);

// ── Selección bulk ──────────────────────────────────────────────────────────
const selectedIds = ref(new Set());
const bulkSending = ref(false);

const isSelectable = (inv) =>
    inv.clientResolved && inv.clientEmail && inv.status !== 'paid' && inv.status !== 'pending';

const disabledReason = (inv) => {
    const reasons = [];
    if (inv.status === 'paid') reasons.push('La factura está pagada');
    if (inv.status === 'pending') reasons.push('La factura aún no está vencida');
    if (!inv.clientResolved) reasons.push('Cliente sin resolver en ConnectUs');
    if (!inv.clientEmail) reasons.push('Cliente sin correo registrado');
    return reasons.join(' · ');
};

const toggleSelect = (inv) => {
    if (!isSelectable(inv)) return;
    if (selectedIds.value.has(inv.id)) {
        selectedIds.value.delete(inv.id);
    } else {
        selectedIds.value.add(inv.id);
    }
    selectedIds.value = new Set(selectedIds.value);
};

const allPageSelected = computed(() => {
    const selectable = invoices.value.filter(isSelectable);
    return selectable.length > 0 && selectable.every(inv => selectedIds.value.has(inv.id));
});

const isIndeterminate = computed(() => {
    const selectable = invoices.value.filter(isSelectable);
    const selectedCount = selectable.filter(inv => selectedIds.value.has(inv.id)).length;
    return selectedCount > 0 && selectedCount < selectable.length;
});

const toggleSelectAll = () => {
    const selectable = invoices.value.filter(isSelectable);
    if (allPageSelected.value) {
        selectable.forEach(inv => selectedIds.value.delete(inv.id));
    } else {
        selectable.forEach(inv => selectedIds.value.add(inv.id));
    }
    selectedIds.value = new Set(selectedIds.value);
};

const clearSelection = () => { selectedIds.value = new Set(); };

const confirmBulkSend = () => {
    const count = selectedIds.value.size;
    ElMessageBox.confirm(
        `Se enviarán las notificaciones aplicables (30, 35, 60 y 90 días) para las ${count} facturas seleccionadas, agrupadas por cliente. ¿Continuar?`,
        'Enviar notificaciones en bloque',
        { confirmButtonText: 'Sí, enviar', cancelButtonText: 'Cancelar', type: 'info' }
    ).then(() => {
        bulkSending.value = true;
        uiStore.startLoading();
        InvoiceService.sendBulkNotifications([...selectedIds.value])
            .then((res) => {
                const d = res.data;
                const total = (d.notice30 ?? 0) + (d.notice35 ?? 0) + (d.notice60 ?? 0) + (d.notice90 ?? 0);
                ElMessage.success(
                    `Notificaciones enviadas — 30d: ${d.notice30}, 35d: ${d.notice35}, 60d: ${d.notice60}, 90d: ${d.notice90} (${total} en total)`
                );
                clearSelection();
                getInvoices();
            })
            .catch(() => ElMessage.error('Error al enviar las notificaciones'))
            .finally(() => { bulkSending.value = false; uiStore.stopLoading(); });
    }).catch(() => { });
};

const statusOptions = [
    { label: 'Pendiente', value: 'pending' },
    { label: 'Vencida', value: 'overdue' },
    { label: 'Pago parcial', value: 'partial_payment' },
    { label: 'Plan de pagos', value: 'payment_plan' },
    { label: 'Pagada', value: 'paid' },
];

const pageKpis = computed(() => {
    const activeInvoices = invoices.value.filter(inv => inv.status !== 'paid');
    const pendingTotal = activeInvoices.reduce((sum, inv) => sum + parseFloat(inv.pendingAmount || 0), 0);
    const overdueCount = activeInvoices.filter(inv => isOverdue(inv)).length;
    const unresolvedCount = activeInvoices.filter(inv => !inv.clientResolved).length;
    const notificationsCount = activeInvoices.reduce((sum, inv) => sum + Number(inv.notificationCount || 0), 0);
    const statusCount = invoices.value.filter(inv => filterStatus.value ? inv.status === filterStatus.value : isOverdue(inv)).length;

    return { pendingTotal, overdueCount, unresolvedCount, notificationsCount, statusCount };
});

const activeStatusLabel = computed(() => {
    const map = {
        overdue: 'Vencidas', paid: 'Pagadas', pending: 'Pendientes',
        partial_payment: 'Pago parcial', payment_plan: 'Plan de pagos',
    };
    return map[filterStatus.value] ?? 'Vencidas';
});

const globalStatusCount = computed(() => {
    if (!globalKpis.value) return null;
    const map = {
        overdue: globalKpis.value.overdueCount,
        paid: globalKpis.value.paidCount,
        pending: globalKpis.value.pendingCount,
        partial_payment: globalKpis.value.partialPaymentCount,
        payment_plan: globalKpis.value.paymentPlanCount,
    };
    return map[filterStatus.value] ?? globalKpis.value.overdueCount;
});

onMounted(() => { getInvoices(); });
onBeforeUnmount(() => { debouncedSearch.cancel(); });

const getInvoices = async () => {
    invoiceStore.setInvoiceFilters({
        number: filterNumber.value,
        client: filterClient.value,
        status: filterStatus.value,
        amountMin: filterAmountMin.value,
        amountMax: filterAmountMax.value,
        page: page.value,
        itemsPerPage: itemsPerPage.value,
    });

    uiStore.startLoading();
    const params = {
        page: page.value,
        itemsPerPage: itemsPerPage.value,
        'order[invoiceDate]': 'desc',
    };
    if (filterNumber.value) params.invoiceNumber = filterNumber.value;
    if (filterClient.value) params.clientName = filterClient.value;
    if (filterStatus.value) params.status = filterStatus.value;
    if (filterAmountMin.value) params['pendingAmount[gte]'] = filterAmountMin.value;
    if (filterAmountMax.value) params['pendingAmount[lte]'] = filterAmountMax.value;

    const kpisParams = { ...params };
    delete kpisParams.page;
    delete kpisParams.itemsPerPage;
    delete kpisParams['order[invoiceDate]'];

    Promise.all([
        InvoiceService.getInvoices(params),
        InvoiceService.getKpis(kpisParams),
    ]).then(([listRes, kpisRes]) => {
        invoices.value = listRes.data['hydra:member'];
        totalResults.value = listRes.data['hydra:totalItems'];
        if (kpisRes) globalKpis.value = kpisRes.data;
        uiStore.stopLoading();
    }).catch(() => uiStore.stopLoading());
};

const debouncedSearch = debounce(() => { page.value = 1; getInvoices(); }, 300);

const onPageChange = (newPage) => { page.value = newPage; getInvoices(); };
const onSizeChange = (newSize) => {
    itemsPerPage.value = newSize;
    page.value = 1;
    getInvoices();
};

const openDetail = (invoice) => {
    router.push({ name: 'invoice', params: { id: invoice.id } });
};

const confirmMarkPaid = (invoice) => {
    ElMessageBox.confirm(
        `¿Marcar la factura ${invoice.invoiceNumber} como pagada?`,
        'Confirmar',
        { confirmButtonText: 'Sí, pagada', cancelButtonText: 'Cancelar', type: 'success' }
    ).then(async () => {
        await InvoiceService.markPaid(invoice.id);
        ElMessage.success('Factura marcada como pagada');
        getInvoices();
    }).catch(() => { });
};

const confirmMarkPaymentPlan = (invoice) => {
    ElMessageBox.confirm(
        `¿Registrar plan de pagos para la factura ${invoice.invoiceNumber}?`,
        'Confirmar',
        { confirmButtonText: 'Sí', cancelButtonText: 'Cancelar', type: 'warning' }
    ).then(async () => {
        await InvoiceService.markPaymentPlan(invoice.id);
        ElMessage.success('Plan de pagos registrado');
        getInvoices();
    }).catch(() => { });
};

const onImportDone = () => {
    showImportDialog.value = false;
    page.value = 1;
    getInvoices();
};

const runProcessNotifications = () => {
    uiStore.startLoading();
    InvoiceService.processNotifications()
        .then((res) => {
            ElMessage.success(`Notificaciones procesadas: ${res.data.processed} facturas`);
        })
        .catch(() => ElMessage.error('Error al procesar notificaciones'))
        .finally(() => uiStore.stopLoading());
};

const runResetTestData = () => {
    ElMessageBox.confirm(
        'Se eliminarán TODAS las facturas y todos los logs de notificación. ¿Continuar?',
        'Reset de datos de prueba',
        { confirmButtonText: 'Sí, borrar todo', cancelButtonText: 'Cancelar', type: 'warning' }
    ).then(() => {
        uiStore.startLoading();
        InvoiceService.resetTestData()
            .then((res) => {
                ElMessage.success(`Reset completado: ${res.data.invoices} facturas, ${res.data.notificationLogs} logs y ${res.data.pdfsDeleted} PDFs eliminados`);
                page.value = 1;
                getInvoices();
            })
            .catch(() => ElMessage.error('Error al resetear los datos'))
            .finally(() => uiStore.stopLoading());
    }).catch(() => { });
};

const statusLabel = (status) => {
    const map = { pending: 'Pendiente', paid: 'Pagada', payment_plan: 'Plan de pagos', overdue: 'Vencida', partial_payment: 'Pago parcial' };
    return map[status] ?? status;
};

const statusTag = (status) => {
    const map = { pending: 'warning', paid: 'success', payment_plan: 'info', overdue: 'danger', partial_payment: '' };
    return map[status] ?? '';
};

const daysLabel = (invoice) => {
    if (!invoice.invoiceDate) return '-';
    const diff = Math.floor((Date.now() - new Date(invoice.invoiceDate)) / 86400000);
    return diff;
};

const daysUntilDue = (invoice) => {
    if (!invoice.dueDate) return null;
    return Math.ceil((new Date(invoice.dueDate) - Date.now()) / 86400000);
};

const isOverdue = (invoice) => {
    if (invoice.status === 'overdue') return true;
    const days = daysUntilDue(invoice);
    return days !== null && days < 0 && invoice.status !== 'paid';
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    const [intPart, decPart] = num.toFixed(2).split('.');
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + decPart + ' €';
};
</script>

<template>
    <div class="invoice-page">
        <HeaderTitle title="Cobros" />

        <div class="d-flex flex-row justify-content-start mb-2 invoice-section-title">
            <div class="d-flex align-items-center">
                <font-awesome-icon icon="list" class="invoice-title-icon" />
                <div class="title-text">Facturas pendientes</div>
            </div>
        </div>

        <el-row>
            <el-card class="card-search w-100 wback invoice-search-card" body-style="padding:0">
                <div class="row">
                    <div class="col-xl-3 col-md-6 col-lg-4">
                        <div class="form-item mt-4 w-100 label-common">
                            <label for="invoice-number" class="w-100">Núm. factura</label>
                            <el-input id="invoice-number" v-model="filterNumber" class="search-input"
                                placeholder="Buscar..." clearable @input="debouncedSearch()" />
                        </div>
                    </div>
                    <div class="col-xl-3 col-md-6 col-lg-4">
                        <div class="form-item mt-4 w-100 label-common pl-5 invoice-filter-spaced">
                            <label for="invoice-client" class="w-100">Cliente</label>
                            <el-input id="invoice-client" v-model="filterClient" class="search-input"
                                placeholder="Buscar por cliente..." clearable @input="debouncedSearch()" />
                        </div>
                    </div>
                    <div class="col-xl-2 col-md-6 col-lg-4">
                        <div class="form-item mt-4 w-100 label-common pl-5 invoice-filter-spaced">
                            <label for="invoice-status" class="w-100">Estado</label>
                            <el-select id="invoice-status" v-model="filterStatus" filterable clearable
                                placeholder="TODOS" @change="page = 1; getInvoices()">
                                <el-option v-for="s in statusOptions" :key="s.value" :label="s.label"
                                    :value="s.value" />
                            </el-select>
                        </div>
                    </div>
                    <div class="col-xl-2 col-md-6 col-lg-4">
                        <div class="form-item mt-4 w-100 label-common pl-5 invoice-filter-spaced">
                            <label for="invoice-amount-min" class="w-100">Pendiente desde</label>
                            <el-input id="invoice-amount-min" v-model="filterAmountMin" class="search-input"
                                placeholder="0 €" type="number" min="0" clearable @input="debouncedSearch()" />
                        </div>
                    </div>
                    <div class="col-xl-2 col-md-6 col-lg-4">
                        <div class="form-item mt-4 w-100 label-common pl-5 invoice-filter-spaced">
                            <label for="invoice-amount-max" class="w-100">Pendiente hasta</label>
                            <el-input id="invoice-amount-max" v-model="filterAmountMax" class="search-input"
                                placeholder="Sin límite" type="number" min="0" clearable @input="debouncedSearch()" />
                        </div>
                    </div>
                    <div class="col-xl-12 col-md-12 col-lg-12 invoice-search-actions">
                        <div class="btn-group-responsive">
                            <button class="btn btn_red btn__new" type="button" @click="showImportDialog = true">
                                Importar Excel
                            </button>
                            <button class="btn btn_yellow btn-search btn__search" type="button"
                                @click="page = 1; getInvoices()">
                                Buscar
                            </button>
                        </div>
                        <div class="btn-group-responsive btn-group-test mt-2">
                            <el-tooltip
                                content="[PRUEBAS] Procesa y envía los correos de notificación para todas las facturas activas"
                                placement="top">
                                <button class="btn btn-test btn-test--notify" type="button"
                                    @click="runProcessNotifications">
                                    <svg style="width:14px;margin-right:5px;" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2">
                                        <path
                                            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3 1.35h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16v.92Z" />
                                    </svg>
                                    Enviar notificaciones
                                </button>
                            </el-tooltip>
                            <el-tooltip content="[PRUEBAS] Borra todas las facturas y logs de notificación"
                                placement="top">
                                <button class="btn btn-test btn-test--reset" type="button" @click="runResetTestData">
                                    <svg style="width:14px;margin-right:5px;" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                        <path d="M10 11v6" />
                                        <path d="M14 11v6" />
                                    </svg>
                                    Reset datos prueba
                                </button>
                            </el-tooltip>
                        </div>
                    </div>
                </div>
            </el-card>
        </el-row>

        <section class="invoice-kpis">
            <article class="invoice-kpi-card magenta">
                <div class="invoice-kpi-icon"><font-awesome-icon icon="euro-sign" /></div>
                <div>
                    <strong>{{ globalKpis ? formatCurrency(globalKpis.totalPendingAmount) : formatCurrency(pageKpis.pendingTotal) }}</strong>
                    <span>Pendiente total</span>
                    <small>Esta página: {{ formatCurrency(pageKpis.pendingTotal) }}</small>
                </div>
            </article>
            <article class="invoice-kpi-card red">
                <div class="invoice-kpi-icon"><font-awesome-icon icon="stopwatch" /></div>
                <div>
                    <strong>{{ globalKpis ? globalStatusCount : pageKpis.statusCount }}</strong>
                    <span>{{ activeStatusLabel }}</span>
                    <small>Esta página: {{ pageKpis.statusCount }}</small>
                </div>
            </article>
            <article class="invoice-kpi-card orange">
                <div class="invoice-kpi-icon"><font-awesome-icon icon="user" /></div>
                <div>
                    <strong>{{ globalKpis ? globalKpis.unresolvedCount : pageKpis.unresolvedCount }}</strong>
                    <span>Clientes sin resolver</span>
                    <small>Esta página: {{ pageKpis.unresolvedCount }}</small>
                </div>
            </article>
            <article class="invoice-kpi-card teal">
                <div class="invoice-kpi-icon"><font-awesome-icon icon="bell" /></div>
                <div>
                    <strong>{{ globalKpis ? globalKpis.notificationsCount : pageKpis.notificationsCount }}</strong>
                    <span>Notificaciones</span>
                    <small>Esta página: {{ pageKpis.notificationsCount }}</small>
                </div>
            </article>
        </section>

        <!-- Barra de acciones bulk -->
        <transition name="bulk-bar">
            <div v-if="selectedIds.size > 0" class="invoice-bulk-bar">
                <span class="invoice-bulk-count">{{ selectedIds.size }} factura{{ selectedIds.size === 1 ? '' : 's' }}
                    seleccionada{{ selectedIds.size === 1 ? '' : 's' }}</span>
                <button class="invoice-bulk-btn invoice-bulk-btn--clear" type="button" @click="clearSelection">Limpiar
                    selección</button>
                <button class="invoice-bulk-btn invoice-bulk-btn--send" type="button" :disabled="bulkSending"
                    @click="confirmBulkSend">
                    <svg style="width:14px;margin-right:5px;" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                    Enviar notificaciones
                </button>
            </div>
        </transition>

        <div class="table table-material invoice-table table-not-mobile">
            <div v-if="invoices.length > 0">
                <table>
                    <colgroup>
                        <col class="invoice-col-check">
                        <col class="invoice-col-invoice">
                        <col class="invoice-col-client">
                        <col class="invoice-col-date">
                        <col class="invoice-col-due-date">
                        <col class="invoice-col-days">
                        <col class="invoice-col-money">
                        <col class="invoice-col-money">
                        <col class="invoice-col-status">
                        <col class="invoice-col-notifications">
                        <col class="invoice-col-actions">
                    </colgroup>
                    <thead>
                        <tr class="justify-content-start gap-2">
                            <th class="invoice-col-check">
                                <el-checkbox :model-value="allPageSelected" :indeterminate="isIndeterminate"
                                    @change="toggleSelectAll" />
                            </th>
                            <th>FACTURA</th>
                            <th>CLIENTE</th>
                            <th>FECHA</th>
                            <th>VENCIMIENTO</th>
                            <th>DÍAS</th>
                            <th>TOTAL</th>
                            <th>PENDIENTE</th>
                            <th>ESTADO</th>
                            <th>NOTIF.</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="inv in invoices" :key="inv.id"
                            :class="{ 'invoice-row--overdue': isOverdue(inv), 'invoice-row--selected': selectedIds.has(inv.id) }">
                            <td class="invoice-check-cell" @click.stop>
                                <el-tooltip :content="disabledReason(inv)" placement="top"
                                    :disabled="!disabledReason(inv)">
                                    <el-checkbox :model-value="selectedIds.has(inv.id)" :disabled="!isSelectable(inv)"
                                        @change="toggleSelect(inv)" />
                                </el-tooltip>
                            </td>
                            <td class="pointer invoice-number-cell" @click="openDetail(inv)">{{ inv.invoiceNumber }}
                            </td>
                            <td class="pointer invoice-client-cell" @click="openDetail(inv)">
                                <div class="invoice-client-content">
                                    <span>{{ inv.clientName }}</span>
                                    <el-tag v-if="!inv.clientResolved" type="danger" size="small">sin resolver</el-tag>
                                    <el-tag v-else-if="!inv.clientEmail" type="warning" size="small">sin correo</el-tag>
                                </div>
                            </td>
                            <td class="pointer invoice-date-cell" @click="openDetail(inv)">{{
                                inv.invoiceDate ? inv.invoiceDate.substring(0, 10) : '-' }}</td>
                            <td class="pointer invoice-date-cell" @click="openDetail(inv)">{{
                                inv.dueDate ? inv.dueDate.substring(0, 10) : '-' }}</td>
                            <td class="pointer" @click="openDetail(inv)">
                                <span :class="{ 'text-danger fw-bold': daysLabel(inv) >= 30 }">{{ daysLabel(inv)
                                    }}</span>
                            </td>
                            <td class="pointer invoice-money-cell" @click="openDetail(inv)">{{ formatCurrency(inv.total)
                                }}</td>
                            <td class="pointer fw-bold invoice-pending-amount" @click="openDetail(inv)">{{
                                formatCurrency(inv.pendingAmount) }}</td>
                            <td class="invoice-status-cell">
                                <el-tag :type="statusTag(inv.status)">{{ statusLabel(inv.status) }}</el-tag>
                            </td>
                            <td class="pointer invoice-notification-cell" @click="openDetail(inv)">
                                <el-tooltip content="Notificaciones enviadas" placement="top">
                                    <span class="invoice-notification-pill">
                                        <font-awesome-icon icon="bell" />
                                        {{ inv.notificationCount ?? 0 }}
                                    </span>
                                </el-tooltip>
                            </td>
                            <td class="invoice-actions-cell">
                                <div class="buttons d-inline-flex invoice-row-actions">
                                    <el-tooltip content="Ver detalle de la factura" placement="top">
                                        <button class="invoice-action-button invoice-action-button--detail"
                                            type="button" title="Ver detalle" @click.stop="openDetail(inv)">
                                            <font-awesome-icon icon="eye" />
                                        </button>
                                    </el-tooltip>
                                    <el-tooltip content="Marcar factura como pagada" placement="top">
                                        <button class="invoice-action-button invoice-action-button--paid" type="button"
                                            :class="{ 'invoice-action-disabled': inv.status === 'paid' }"
                                            :disabled="inv.status === 'paid'" title="Marcar como pagada"
                                            @click.stop="confirmMarkPaid(inv)">
                                            <font-awesome-icon icon="check" />
                                        </button>
                                    </el-tooltip>
                                    <el-tooltip content="Registrar plan de pagos" placement="top">
                                        <button class="invoice-action-button invoice-action-button--plan" type="button"
                                            :class="{ 'invoice-action-disabled': inv.status === 'paid' || inv.status === 'payment_plan' }"
                                            :disabled="inv.status === 'paid' || inv.status === 'payment_plan'"
                                            title="Registrar plan de pagos" @click.stop="confirmMarkPaymentPlan(inv)">
                                            <font-awesome-icon icon="arrows-rotate" />
                                        </button>
                                    </el-tooltip>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="invoice-empty-state">
                <h5>No se han encontrado facturas con los filtros especificados.</h5>
            </div>
        </div>

        <div class="table-mobile invoice-mobile-list">
            <div v-if="invoices.length > 0">
                <article v-for="inv in invoices" :key="inv.id" class="invoice-card pointer" @click="openDetail(inv)">
                    <div class="invoice-card__header">
                        <div class="invoice-card__identity">
                            <strong>{{ inv.invoiceNumber }}</strong>
                            <div class="invoice-card__meta">
                                <span>{{ inv.invoiceDate ? inv.invoiceDate.substring(0, 10) : '-' }}</span>
                                <el-tag :type="statusTag(inv.status)">{{ statusLabel(inv.status) }}</el-tag>
                            </div>
                        </div>
                        <div class="invoice-card__quick-actions" @click.stop>
                            <el-tooltip content="Ver detalle de la factura" placement="top">
                                <button class="invoice-action-button invoice-action-button--detail" type="button"
                                    title="Ver detalle" @click.stop="openDetail(inv)">
                                    <font-awesome-icon icon="eye" />
                                </button>
                            </el-tooltip>
                            <el-tooltip content="Marcar factura como pagada" placement="top">
                                <button class="invoice-action-button invoice-action-button--paid" type="button"
                                    :class="{ 'invoice-action-disabled': inv.status === 'paid' }"
                                    :disabled="inv.status === 'paid'" title="Marcar como pagada"
                                    @click.stop="confirmMarkPaid(inv)">
                                    <font-awesome-icon icon="check" />
                                </button>
                            </el-tooltip>
                            <el-tooltip content="Registrar plan de pagos" placement="top">
                                <button class="invoice-action-button invoice-action-button--plan" type="button"
                                    :class="{ 'invoice-action-disabled': inv.status === 'paid' || inv.status === 'payment_plan' }"
                                    :disabled="inv.status === 'paid' || inv.status === 'payment_plan'"
                                    title="Registrar plan de pagos" @click.stop="confirmMarkPaymentPlan(inv)">
                                    <font-awesome-icon icon="arrows-rotate" />
                                </button>
                            </el-tooltip>
                        </div>
                    </div>
                    <div class="invoice-card__client">
                        <span>{{ inv.clientName }}</span>
                        <el-tag v-if="!inv.clientResolved" type="danger" size="small">sin resolver</el-tag>
                        <el-tag v-else-if="!inv.clientEmail" type="warning" size="small">sin correo</el-tag>
                    </div>
                    <dl class="invoice-card__data">
                        <div>
                            <dt>Vencimiento</dt>
                            <dd>{{ inv.dueDate ? inv.dueDate.substring(0, 10) : '-' }}</dd>
                        </div>
                        <div>
                            <dt>Días</dt>
                            <dd :class="{ 'text-danger': daysLabel(inv) >= 30 }">{{ daysLabel(inv) }}</dd>
                        </div>
                        <div>
                            <dt>Notif.</dt>
                            <dd>
                                <el-tooltip content="Notificaciones enviadas" placement="top">
                                    <span class="invoice-notification-pill">
                                        <font-awesome-icon icon="bell" />
                                        {{ inv.notificationCount ?? 0 }}
                                    </span>
                                </el-tooltip>
                            </dd>
                        </div>
                        <div>
                            <dt>Pendiente</dt>
                            <dd class="invoice-pending-amount">{{ formatCurrency(inv.pendingAmount) }}</dd>
                        </div>
                    </dl>
                </article>
            </div>
            <div v-else class="invoice-empty-state">
                <h5>No se han encontrado facturas.</h5>
            </div>
        </div>

        <SharedElementPagination v-if="invoices.length > 0" class="pagination invoice-pagination"
            v-model:current-page="page" v-model:page-size="itemsPerPage" :page-size-options="[10, 20, 50, 100, 200]"
            layout="prev, pager, next, sizes" :total="totalResults" show-always @current-change="onPageChange"
            @size-change="onSizeChange" />

        <el-dialog v-model="showImportDialog" width="601" :show-close="false" :lock-scroll="true"
            @close="showImportDialog = false">
            <InvoiceImportDialog v-if="showImportDialog" @close-dialog="showImportDialog = false"
                @done="onImportDone" />
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
.invoice-page {
    padding: 0 22px 32px;
}

:deep(#header-title) {
    margin-bottom: 0;
}

@media (min-width: 993px) and (max-width: 1440px) {
    .invoice-page {
        padding-left: 0;
    }
}

.invoice-kpis,
.invoice-table,
.invoice-mobile-list {
    max-width: 1680px;
    margin-left: auto;
    margin-right: auto;
}

.invoice-section-title {
    max-width: 1680px;
    margin-left: auto;
    margin-right: auto;
}

.invoice-title-icon {
    width: 30px;
    height: 30px;
    color: $bg-blue;
}

.invoice-search-card {
    max-width: 1680px;
    margin: 0 auto;
    border-radius: 28px;
    overflow: hidden;
    background-image:
        linear-gradient(90deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.93) 42%, rgba(255, 255, 255, 0.36) 68%, rgba(255, 255, 255, 0.1) 100%),
        url('~@/assets/images/invoice-search-bg.jpg') !important;
    background-position: center right;
    background-size: cover;
    background-repeat: no-repeat;
}

.invoice-search-card .row {
    min-height: 160px;
    padding: 18px;
    align-items: flex-start;
}

.invoice-search-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: auto;
    margin-top: 18px;
}

.btn-group-responsive {
    display: flex;
    gap: 1rem;
    align-items: center;
    width: 100%;
    justify-content: flex-end;
}

.btn__new {
    width: 220px;
    height: 3em;
    font-weight: bolder;
    line-height: 1.05;
    text-transform: uppercase;
    white-space: normal;
}

.btn__search {
    width: 220px;
    height: 3em;
}

.invoice-kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    margin-top: 18px;
    margin-bottom: 22px;
}

.invoice-kpi-card {
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 82px;
    padding: 14px;
    background: #fff;
    border: 1px solid #e5e7ef;
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.invoice-kpi-icon {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    border-radius: 8px;
    color: #fff;
    font-size: 28px;
}

.invoice-kpi-card.magenta .invoice-kpi-icon {
    background: linear-gradient(135deg, #ff0f78 0%, #f4215f 100%);
}

.invoice-kpi-card.red .invoice-kpi-icon {
    background: linear-gradient(135deg, #ff2936 0%, #ef233c 100%);
}

.invoice-kpi-card.orange .invoice-kpi-icon {
    background: linear-gradient(135deg, #ffaf00 0%, #fb8e00 100%);
}

.invoice-kpi-card.teal .invoice-kpi-icon {
    background: linear-gradient(135deg, #11bfc6 0%, #008ea0 100%);
}

.invoice-kpi-card strong {
    display: block;
    font-size: 22px;
    line-height: 1.1;
    color: #f4215f;
}

.invoice-kpi-card span,
.invoice-kpi-card small {
    display: block;
    color: #4b5563;
}

.invoice-kpi-card span {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
}

.invoice-kpi-card small {
    font-size: 11px;
}

.invoice-bulk-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    position: sticky;
    top: 56px;
    z-index: 100;
    background: #fff;
    border: 1px solid #e0e7ff;
    border-radius: 10px;
    padding: 10px 16px;
    margin: 8px auto 10px;
    max-width: 1680px;
    box-shadow: 0 4px 16px rgba(71, 76, 230, 0.12);
}

.invoice-bulk-count {
    flex: 1;
    font-weight: 600;
    color: #474CE6;
    font-size: 14px;
}

.invoice-bulk-btn {
    display: inline-flex;
    align-items: center;
    padding: 7px 16px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: opacity 0.15s;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        opacity: 0.85;
    }
}

.invoice-bulk-btn--clear {
    background: #f3f4f6;
    color: #4b5563;
}

.invoice-bulk-btn--send {
    background: #474CE6;
    color: #fff;
}

.bulk-bar-enter-active,
.bulk-bar-leave-active {
    transition: all 0.2s ease;
}

.bulk-bar-enter-from,
.bulk-bar-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.invoice-row--selected {
    background: #f0f1ff !important;
}

.invoice-col-check {
    width: 36px;
}

.invoice-table th.invoice-col-check,
.invoice-check-cell {
    padding-left: 0;
    padding-right: 0;
    text-align: center;
}

.invoice-table th.invoice-col-check :deep(.el-checkbox),
.invoice-check-cell :deep(.el-checkbox) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: auto;
    margin: 0;
    vertical-align: middle;
}

.invoice-table {
    margin-top: 8px;
    overflow-x: auto;

    table {
        thead {
            tr {
                th {
                    padding: 0 !important;
                    margin: 0 !important;
                }
            }
        }

        tbody {
            td {
                letter-spacing: -0.05em;
                font-size: 0.9em;
                font-weight: 700;
            }
        }
    }
}

.invoice-table table {
    min-width: 1180px;
    table-layout: auto;
}

.invoice-table th,
.invoice-table td {
    vertical-align: middle;
}

.invoice-col-invoice {
    width: 120px;
}

.invoice-col-client {
    width: auto;
}

.invoice-col-date {
    width: 105px;
}

.invoice-col-due-date {
    width: 120px;
}

.invoice-col-days {
    width: 58px;
}

.invoice-col-money {
    width: 122px;
}

.invoice-col-status {
    width: 112px;
}

.invoice-col-notifications {
    width: 70px;
}

.invoice-col-actions {
    width: 120px;
}

.invoice-number-cell,
.invoice-date-cell,
.invoice-money-cell,
.invoice-pending-amount {
    color: #1f2937;
    font-weight: 700;
    white-space: nowrap;
}

.invoice-pending-amount {
    color: #f4213d;
}

.invoice-client-cell {
    min-width: 0;
}

.invoice-client-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    min-width: 0;
}

.invoice-client-content span {
    min-width: 0;
    overflow-wrap: normal;
    word-break: normal;
}

.invoice-row-actions {
    display: inline-grid !important;
    grid-template-columns: repeat(3, 28px);
    align-items: center;
    justify-content: start !important;
    gap: 8px;
}

.invoice-action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    color: #fff;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.invoice-action-button:hover,
.invoice-action-button:focus {
    box-shadow: 0 5px 12px rgba(31, 41, 55, 0.18);
    transform: translateY(-1px);
}

.invoice-action-button--detail {
    background: #474CE6;
}

.invoice-action-button--paid {
    background: #27ae60;
}

.invoice-action-button--plan {
    background: #f7d20e;
}

.invoice-action-button svg {
    display: block;
    width: 1em;
    height: 1em;
}

.invoice-action-disabled {
    cursor: not-allowed;
    opacity: 0.35;
}

.invoice-notification-pill {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    min-width: 40px;
    color: #474CE6;
    font-weight: 800;
}

.invoice-status-cell,
.invoice-notification-cell,
.invoice-actions-cell {
    text-align: left;
}

.invoice-empty-state {
    padding: 28px;
    color: #667085;
}

.invoice-pagination {
    max-width: 1680px;
    margin: 8px auto 28px;
    padding: 12px 0 0;
    display: flex;
    justify-content: center;
}

.invoice-mobile-list {
    display: none;
}

.invoice-card {
    position: relative;
    padding: 16px;
    margin: 18px 0;
    background: #fff;
    border: 1px solid #e5e7ef;
    border-radius: 16px;
    box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.invoice-card__header,
.invoice-card__client {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.invoice-card__identity {
    flex: 1 1 auto;
    min-width: 0;
}

.invoice-card__identity strong,
.invoice-card__meta span {
    display: block;
}

.invoice-card__identity strong {
    color: #3445f4;
}

.invoice-card__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 8px;
    margin-top: 3px;
}

.invoice-card__meta span {
    color: #667085;
    font-size: 13px;
}

.invoice-card__client {
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    margin-top: 10px;
    color: #1f2937;
}

.invoice-card__quick-actions {
    display: flex;
    flex: 0 0 auto;
    justify-content: flex-end;
    gap: 8px;
    z-index: 1;
}

.invoice-card__data {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin: 14px 0 0;
}

.invoice-card__data div {
    padding: 10px;
    background: #f8f9ff;
    border-radius: 8px;
}

.invoice-card__data dt,
.invoice-card__data dd {
    margin: 0;
}

.invoice-card__data dt {
    color: #667085;
    font-size: 12px;
}

.invoice-card__data dd {
    color: #1f2937;
    font-weight: 700;
}

.table-mobile {
    display: none;
}

th {
    white-space: nowrap;
}

@media (max-width: 767px) {
    .invoice-page {
        padding: 0 14px 24px;
    }

    .invoice-search-card {
        border-radius: 24px;
    }

    .invoice-search-card .row {
        padding: 18px 14px;
        row-gap: 12px;
    }

    .invoice-search-card .form-item.mt-4 {
        margin-top: 0 !important;
    }

    .invoice-filter-spaced {
        padding-left: 0 !important;
    }

    .invoice-search-actions {
        flex-direction: column;
        align-items: stretch;
        min-height: auto;
        margin-top: 14px;
    }

    .btn-group-responsive {
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 10px;
        width: 100%;
    }

    .btn-group-responsive button {
        width: 100% !important;
        max-width: none;
    }

    .invoice-kpis {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .invoice-kpi-card {
        align-items: flex-start;
        flex-direction: column;
        min-height: 0;
    }

    .invoice-kpi-icon {
        width: 52px;
        height: 52px;
        font-size: 22px;
    }

    .invoice-kpi-card strong {
        font-size: 22px;
    }

    .table-not-mobile {
        display: none;
    }

    .table-mobile {
        display: block;
    }

    .invoice-card__header {
        align-items: flex-start;
    }

    .invoice-card__quick-actions {
        gap: 0px;
    }

    .invoice-card__identity strong,
    .invoice-card__meta span,
    .invoice-card__client span {
        overflow-wrap: anywhere;
    }

    .invoice-card__meta .el-tag {
        flex: 0 0 auto;
        max-width: 100%;
        white-space: normal;
        height: auto;
        line-height: 1.2;
    }

    .invoice-card__data {
        grid-template-columns: 1fr;
    }

    .btn-group-test {
        margin-top: 10px !important;
    }

    .btn-test {
        justify-content: center;
        min-height: 42px;
        white-space: normal;
        text-align: center;
    }
}

.btn-group-test {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.btn-test {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    padding: 5px 12px;
    border-radius: 6px;
    border: 1px dashed;
    font-weight: 500;
    cursor: pointer;
    opacity: 0.85;

    &:hover {
        opacity: 1;
    }
}

.btn-test--notify {
    color: #1a7f5a;
    border-color: #1a7f5a;
    background: #f0fdf4;
}

.btn-test--reset {
    color: #b91c1c;
    border-color: #b91c1c;
    background: #fef2f2;
}
</style>
